'use client'
/**
 * Google "add as a preferred source" button (docs/plans/GROWTH-PLAN.md G-13).
 *
 * Google's publisher runtime (https://news.google.com/swg/js/v1/publisher.js)
 * turns every `[google-add-preferred-source-btn]` element into its own button.
 * A reader who clicks it gets a news.google.com popup that adds ryan-realty.com
 * to their Top Stories preferred sources.
 *
 * LAZY, TWICE. Nothing loads until the footer is within 400px of the viewport,
 * and then next/script loads it with strategy="lazyOnload" (browser idle, after
 * hydration). A visit that never reaches the footer never fetches the script.
 *
 * CLIENT NAVIGATION. The runtime scans for button elements once, when it first
 * runs. A later page renders a fresh element the runtime has not seen, so
 * onReady (fires on every mount once the script has loaded) calls
 * window.PREFERRED_SOURCE.api.init(), which only claims elements that are not
 * yet marked data-initialized.
 *
 * CSP. This needs https://news.google.com in script-src (the script) and
 * frame-src (its /swg/ui/v1/serviceiframe and /toastiframe). The button logo
 * comes from www.gstatic.com and is already covered by img-src https:. The
 * script makes no fetch, XHR or beacon calls of its own. Evidence is in the
 * CSP comment in next.config.ts and in scripts/check-csp.mjs.
 */
import { useEffect, useRef, useState } from 'react'
import Script from 'next/script'

export const PREFERRED_SOURCE_SCRIPT_SRC = 'https://news.google.com/swg/js/v1/publisher.js'

type PreferredSourceApi = { init: (opts?: Record<string, unknown>) => void }

declare global {
  interface Window {
    PREFERRED_SOURCE?: unknown[] | { api?: PreferredSourceApi }
  }
}

/** Claim any button element the runtime has not initialized yet. Never throws. */
export function initPreferredSourceButtons(win: Window | undefined = typeof window === 'undefined' ? undefined : window): boolean {
  const queue = win?.PREFERRED_SOURCE
  if (!queue || Array.isArray(queue)) return false
  try {
    queue.api?.init()
    return Boolean(queue.api)
  } catch {
    return false
  }
}

export function V3PreferredSource() {
  const ref = useRef<HTMLDivElement>(null)
  const [near, setNear] = useState(false)

  useEffect(() => {
    const el = ref.current
    // No IntersectionObserver means a browser too old for the runtime anyway.
    if (!el || near || typeof IntersectionObserver === 'undefined') return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true)
          obs.disconnect()
        }
      },
      { rootMargin: '400px 0px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [near])

  return (
    <div className="v3-footer__preferred-source">
      <div ref={ref} google-add-preferred-source-btn="" data-theme="light" data-lang="en" />
      {near ? (
        <Script
          id="google-preferred-source"
          src={PREFERRED_SOURCE_SCRIPT_SRC}
          strategy="lazyOnload"
          onReady={() => {
            initPreferredSourceButtons()
          }}
        />
      ) : null}
    </div>
  )
}
